/**
 * Form creazione/modifica fornitore.
 *
 * Se l'URL contiene :id, carica il fornitore esistente (modalità modifica).
 * Altrimenti è in modalità creazione.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface FornitoreFormData {
  ragione_sociale: string;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  partita_iva: string;
  email: string;
  telefono: string;
  pec: string;
  codice_sdi: string;
  indirizzo: string;
  civico: string;
  cap: string;
  comune: string;
  provincia: string;
  nazione: string;
}

const EMPTY_FORM: FornitoreFormData = {
  ragione_sociale: '',
  nome: '',
  cognome: '',
  codice_fiscale: '',
  partita_iva: '',
  email: '',
  telefono: '',
  pec: '',
  codice_sdi: '',
  indirizzo: '',
  civico: '',
  cap: '',
  comune: '',
  provincia: '',
  nazione: '',
};

/**
 * Form creazione/modifica fornitore con campi anagrafici.
 */
const FornitoreForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<FornitoreFormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Carica dati fornitore esistente se in modalità modifica. */
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void (async () => {
      try {
        const response = await fetch(`/api/v1/fornitori/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
          },
        });
        if (!response.ok) throw new Error(`Errore ${response.status}`);
        const data = await response.json();
        setForm({
          ragione_sociale: data.ragione_sociale ?? '',
          nome: data.nome ?? '',
          cognome: data.cognome ?? '',
          codice_fiscale: data.codice_fiscale ?? '',
          partita_iva: data.partita_iva ?? '',
          email: data.email ?? '',
          telefono: data.telefono ?? '',
          pec: data.pec ?? '',
          codice_sdi: data.codice_sdi ?? '',
          indirizzo: data.indirizzo ?? '',
          civico: data.civico ?? '',
          cap: data.cap ?? '',
          comune: data.comune ?? '',
          provincia: data.provincia ?? '',
          nazione: data.nazione ?? '',
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore caricamento');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /** Aggiorna un campo del form. */
  const handleChange = (field: keyof FornitoreFormData, value: string) => {
    setForm((prev: FornitoreFormData) => ({ ...prev, [field]: value }));
  };

  /** Invia il form (POST per creazione, PATCH per modifica). */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Costruisce il payload inviando solo i campi con valore non vuoto
    const payload: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(form) as [string, string][]) {
      payload[key] = value.trim() || null;
    }

    try {
      const url = isEdit ? `/api/v1/fornitori/${id}` : '/api/v1/fornitori';
      const method = isEdit ? 'PATCH' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data = await response.json();
      navigate(`/fornitori/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          {isEdit ? 'Modifica fornitore' : 'Nuovo fornitore'}
        </h1>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Dati anagrafici</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ragione_sociale">Ragione sociale</Label>
                <Input
                  id="ragione_sociale"
                  value={form.ragione_sociale}
                  onChange={(e) =>
                    handleChange('ragione_sociale', e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partita_iva">Partita IVA</Label>
                <Input
                  id="partita_iva"
                  value={form.partita_iva}
                  onChange={(e) =>
                    handleChange('partita_iva', e.target.value)
                  }
                  maxLength={11}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome</Label>
                <Input
                  id="cognome"
                  value={form.cognome}
                  onChange={(e) => handleChange('cognome', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codice_fiscale">Codice fiscale</Label>
                <Input
                  id="codice_fiscale"
                  value={form.codice_fiscale}
                  onChange={(e) =>
                    handleChange('codice_fiscale', e.target.value)
                  }
                  maxLength={16}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codice_sdi">Codice SDI</Label>
                <Input
                  id="codice_sdi"
                  value={form.codice_sdi}
                  onChange={(e) =>
                    handleChange('codice_sdi', e.target.value)
                  }
                  maxLength={7}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pec">PEC</Label>
                <Input
                  id="pec"
                  type="email"
                  value={form.pec}
                  onChange={(e) => handleChange('pec', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Telefono</Label>
                <Input
                  id="telefono"
                  value={form.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="indirizzo">Indirizzo</Label>
                <Input
                  id="indirizzo"
                  value={form.indirizzo}
                  onChange={(e) => handleChange('indirizzo', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="civico">Civico</Label>
                <Input
                  id="civico"
                  value={form.civico}
                  onChange={(e) => handleChange('civico', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cap">CAP</Label>
                <Input
                  id="cap"
                  value={form.cap}
                  onChange={(e) => handleChange('cap', e.target.value)}
                  maxLength={5}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="comune">Comune</Label>
                <Input
                  id="comune"
                  value={form.comune}
                  onChange={(e) => handleChange('comune', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provincia">Provincia</Label>
                <Input
                  id="provincia"
                  value={form.provincia}
                  onChange={(e) => handleChange('provincia', e.target.value)}
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nazione">Nazione</Label>
                <Input
                  id="nazione"
                  value={form.nazione}
                  onChange={(e) => handleChange('nazione', e.target.value)}
                  maxLength={2}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Salvataggio…' : isEdit ? 'Salva modifiche' : 'Crea fornitore'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default FornitoreForm;
